import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Plus, Building, ArrowRight, Eye } from "lucide-react";
import { EmailProviderCard } from "@/components/settings/EmailProviderCard";

const EMPTY = {
  commercial_name: "",
  legal_name: "",
  rfc: "",
  email: "",
  phone: "",
  address: "",
  primary_color: "#0F172A",
  plan_name: "starter",
  account_status: "active",
};

export default function Tenants() {
  const { selectTenant, activeTenantId } = useTenant();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  async function fetchList() {
    setLoading(true);
    try {
      const { data } = await api.get("/tenants", { params: { search: search || undefined } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/tenants", form);
      toast.success("Tenant creado. Ya cuenta con sucursal principal y roles por defecto.");
      setSheetOpen(false);
      setForm(EMPTY);
      fetchList();
      // auto-select the new tenant
      selectTenant(data.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  function enterAsTenant(tenantId) {
    selectTenant(tenantId);
    toast.success("Cambiado a tenant seleccionado");
    navigate("/dashboard");
  }

  return (
    <div className="space-y-6" data-testid="page-tenants">
      <PageHeader
        title="Tenants"
        description="Talleres cliente administrados por VRIntegrales. Cada uno tiene datos, sucursales, usuarios y branding propios."
        actions={
          <Button className="btn-tenant" onClick={() => setSheetOpen(true)} data-testid="btn-new-tenant">
            <Plus className="h-4 w-4 mr-2" /> Nuevo tenant
          </Button>
        }
      />

      <EmailProviderCard />

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar tenant por nombre, RFC…"
            className="pl-9 h-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-tenants"
          />
        </div>
        <div className="text-xs text-muted-foreground ml-auto" data-testid="tenants-count">
          {items.length} tenant{items.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="Sin tenants aún"
            description="Crea tu primer taller cliente. Podrás configurar branding, sucursales y usuarios en seguida."
            icon={Building}
            action={
              <Button className="btn-tenant" onClick={() => setSheetOpen(true)} data-testid="empty-new-tenant">
                <Plus className="h-4 w-4 mr-2" /> Crear tenant
              </Button>
            }
          />
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Taller</TableHead>
                <TableHead>RFC</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Estatus</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} className="tbl-row" data-testid={`row-tenant-${t.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div
                        className="h-8 w-8 rounded flex items-center justify-center text-white text-[11px] font-semibold"
                        style={{ background: t.primary_color }}
                      >
                        {(t.commercial_name || "?").slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium">{t.commercial_name}</div>
                        <div className="text-xs text-muted-foreground">{t.legal_name || ""}</div>
                      </div>
                      {activeTenantId === t.id && (
                        <Badge className="bg-tenant text-white hover:bg-tenant">Activo</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="mono text-xs">{t.rfc || "—"}</TableCell>
                  <TableCell className="text-sm">
                    <div>{t.email || "—"}</div>
                    <div className="text-xs text-muted-foreground">{t.phone || ""}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="capitalize">{t.plan_name}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={
                        t.account_status === "active"
                          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                          : t.account_status === "trial"
                          ? "bg-blue-100 text-blue-800 hover:bg-blue-100"
                          : "bg-red-100 text-red-800 hover:bg-red-100"
                      }
                    >
                      {t.account_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/tenants/${t.id}`)}
                      data-testid={`btn-view-tenant-${t.id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" /> Ver
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => enterAsTenant(t.id)}
                      data-testid={`btn-enter-tenant-${t.id}`}
                    >
                      Operar <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="sheet-tenant">
          <SheetHeader>
            <SheetTitle>Nuevo tenant</SheetTitle>
            <SheetDescription>Se creará automáticamente su sucursal principal y roles internos.</SheetDescription>
          </SheetHeader>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Nombre comercial *</Label>
              <Input required value={form.commercial_name} onChange={(e) => setForm({ ...form, commercial_name: e.target.value })} data-testid="tenant-commercial-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Razón social</Label>
                <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="tenant-legal-name" />
              </div>
              <div className="space-y-2">
                <Label>RFC</Label>
                <Input value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })} className="mono uppercase" data-testid="tenant-rfc" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="tenant-email" />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="tenant-phone" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Dirección</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="tenant-address" />
            </div>
            <div className="space-y-2">
              <Label>Color primario (branding)</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                  className="h-10 w-14 rounded border border-border cursor-pointer"
                  data-testid="tenant-color-picker"
                />
                <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="mono uppercase" />
              </div>
            </div>
            <SheetFooter>
              <Button type="button" variant="outline" onClick={() => setSheetOpen(false)} data-testid="tenant-cancel">Cancelar</Button>
              <Button type="submit" disabled={saving} className="btn-tenant" data-testid="tenant-save">
                {saving ? "Creando…" : "Crear tenant"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
